import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    Button,
    Stack,
    InputAdornment,
    Avatar,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Menu,
    ListItemIcon
} from '@mui/material';
import { Search, Plus, SquarePen, Eye, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllHubsLocations } from '../../Services/HubsLocations/HubsLocationsApi';
import { categoryActiveApi, subCategoryActiveApi } from '../../Services/CategoryManagement/CategoryManagementApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetAllHubsLocations = () => {
    const navigate = useNavigate();

    const [hubsLocations, setHubsLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    // Filter states
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedSubCategory, setSelectedSubCategory] = useState("");

    // Pagination State
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedHubLocation, setMenuSelectedHubLocation] = useState(null);

    const handleMenuOpen = (event, item) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedHubLocation(item);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedHubLocation(null);
    };

    const handleViewDetails = () => {
        handleMenuClose();
        if (menuSelectedHubLocation) {
            navigate(`/hubs-locations/view-details/${menuSelectedHubLocation.id}`, { state: { id: menuSelectedHubLocation.id } });
        }
    };

    const handleEdit = () => {
        handleMenuClose();
        if (menuSelectedHubLocation) {
            navigate(`/hubs-locations/edit/${menuSelectedHubLocation.id}`, { state: { hubLocation: menuSelectedHubLocation } });
        }
    };

    // Load active categories for filter
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await categoryActiveApi();
                if (res && res.success) {
                    setCategories(res.data || []);
                }
            } catch (err) {
                console.error("Error fetching categories:", err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch subcategories when selectedCategory changes
    useEffect(() => {
        if (!selectedCategory) {
            setSubCategories([]);
            setSelectedSubCategory("");
            return;
        }

        const fetchSubCats = async () => {
            try {
                const res = await subCategoryActiveApi(selectedCategory);
                if (res && res.success) {
                    setSubCategories(res.data || []);
                }
            } catch (err) {
                console.error("Error fetching subcategories:", err);
            }
        };
        fetchSubCats();
    }, [selectedCategory]);

    const fetchHubsLocations = useCallback(async (isSearch = false, signal) => {
        try {
            setLoading(true);
            const limitParam = rowsPerPage !== 10 ? rowsPerPage : undefined;
            const response = await getAllHubsLocations(
                page,
                limitParam,
                searchQuery,
                selectedCategory,
                selectedSubCategory,
                { signal }
            );

            if (response && response.success) {
                setHubsLocations(response.data || []);
                setTotalRecords(response.totalRecords || 0);
            } else {
                if (!signal?.aborted) setError("Failed to load hubs locations.");
            }
        } catch (err) {
            if (!signal?.aborted && err.name !== 'CanceledError') {
                setError("Failed to fetch hubs locations. Please try again later.");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [page, rowsPerPage, searchQuery, selectedCategory, selectedSubCategory]);

    const prevSearchQuery = useRef(searchQuery);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const isSearchChange = prevSearchQuery.current !== searchQuery;
        prevSearchQuery.current = searchQuery;

        if (isSearchChange) {
            const timer = setTimeout(() => {
                fetchHubsLocations(true, signal);
            }, 400);
            return () => {
                clearTimeout(timer);
                controller.abort();
            };
        } else {
            fetchHubsLocations(false, signal);
            return () => {
                controller.abort();
            };
        }
    }, [fetchHubsLocations, searchQuery]);



    const parseSuitability = (items) => {
        if (!items) return [];
        if (Array.isArray(items)) return items;
        try {
            return JSON.parse(items);
        } catch {
            return [];
        }
    };

    const parseFaqs = (faqs) => {
        if (!faqs) return [];
        if (Array.isArray(faqs)) return faqs;
        try {
            return JSON.parse(faqs);
        } catch {
            return [];
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2.25 }, bgcolor: '#FBFBFB', minHeight: '100%' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                    <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.2rem', md: '1.3rem' }, fontWeight: 600, lineHeight: 1.25 }}>
                        Hubs Locations
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '0.74rem', mt: 0.25 }}>
                        Manage directory hubs, landing content, and retreat listings
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        disableElevation
                        disableRipple
                        variant="contained"
                        startIcon={<Plus size={15} />}
                        onClick={() => navigate('/hubs-locations/add')}
                        sx={{
                            bgcolor: '#FFBD65',
                            px: 2,
                            height: '32px',
                            color: '#1a1a1a',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: '6px',
                            boxShadow: 'none',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#F5A742', boxShadow: 'none' }
                        }}
                    >
                        Add Hub Location
                    </Button>
                </Stack>
            </Box>

            {/* Filter & Search Bar */}
            <Paper elevation={0} sx={{ p: '10px 14px', mb: 2, borderRadius: '6px', border: '1px solid #E8E8E8', bgcolor: '#FFFFFF', boxShadow: 'none' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap">
                    <TextField
                        placeholder="Search by location name..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        size="small"
                        sx={{
                            width: { xs: '100%', sm: 260 },
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '34px',
                                fontSize: '0.75rem',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                                    <Search size={15} color="#888" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
                        <InputLabel sx={{ fontSize: '0.75rem', top: '-3px', '&.MuiInputLabel-shrink': { top: 0 } }}>Category</InputLabel>
                        <Select
                            value={selectedCategory}
                            label="Category"
                            onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setPage(1);
                            }}
                            sx={{
                                borderRadius: '6px',
                                height: '34px',
                                fontSize: '0.75rem',
                                '& .MuiSelect-select': { py: '6px' },
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.75rem' }}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedCategory && (
                        <FormControl size="small" sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
                            <InputLabel sx={{ fontSize: '0.75rem', top: '-3px', '&.MuiInputLabel-shrink': { top: 0 } }}>SubCategory</InputLabel>
                            <Select
                                value={selectedSubCategory}
                                label="SubCategory"
                                onChange={(e) => {
                                    setSelectedSubCategory(e.target.value);
                                    setPage(1);
                                }}
                                sx={{
                                    borderRadius: '6px',
                                    height: '34px',
                                    fontSize: '0.75rem',
                                    '& .MuiSelect-select': { py: '6px' },
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                                }}
                            >
                                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All SubCategories</MenuItem>
                                {subCategories.map((sub) => (
                                    <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: '0.75rem' }}>
                                        {sub.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {(searchQuery || selectedCategory || selectedSubCategory) && (
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchQuery("");
                                setSelectedCategory("");
                                setSelectedSubCategory("");
                                setPage(1);
                            }}
                            sx={{
                                height: '34px',
                                px: 1.5,
                                fontSize: '0.72rem',
                                color: '#64748B',
                                textTransform: 'none',
                                border: '1px solid #E2E8F0',
                                borderRadius: '6px',
                                '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' }
                            }}
                        >
                            Reset Filters
                        </Button>
                    )}
                </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem', py: 0.5 }}>{error}</Alert>}

            {/* Content Table */}
            <Paper sx={{ borderRadius: "6px", border: "1px solid #E8E8E8", overflow: "hidden", boxShadow: "none" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 1450 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF2F6' }}>
                                {[
                                    { label: 'S.No', align: 'left' },
                                    { label: 'Image', align: 'left' },
                                    { label: 'Hub Name', align: 'left' },
                                    { label: 'Location Name', align: 'left' },
                                    { label: 'Type', align: 'left' },
                                    { label: 'Category', align: 'left' },
                                    { label: 'SubCategory', align: 'left' },
                                    { label: 'Heading', align: 'left' },
                                    { label: 'Suitability', align: 'left' },
                                    { label: 'FAQs', align: 'left' },
                                    { label: 'Created By', align: 'left' },
                                    { label: 'Updated By', align: 'left' },
                                    { label: 'Created At', align: 'left' },
                                    { label: 'Updated At', align: 'left' },
                                    { label: 'Actions', align: 'right' },
                                ].map((h) => (
                                    <TableCell
                                        key={h.label}
                                        align={h.align}
                                        sx={{
                                            fontWeight: 600,
                                            color: '#475569',
                                            fontSize: '0.67rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            py: 1,
                                            px: 1.25,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {h.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={22} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : hubsLocations.length > 0 ? (
                                hubsLocations.map((row, idx) => {
                                    const suitList = parseSuitability(row.suitabilityItems);
                                    const rowImg = row.images ? (Array.isArray(row.images) ? row.images[0] : row.images) : row.image;
                                    const rowName = row.hubName || row.name || '-';
                                    const rowLoc = row.location || row.locationName || (row.town && row.state ? `${row.town}, ${row.state}` : row.town || row.state) || '-';
                                    const rowType = row.placementType || row.type || 'retreat';
                                    const rowCatName = row.category?.name || (typeof row.category === 'string' ? row.category : null) || row.categoryId || 'N/A';
                                    const rowSubName = row.subCategory?.name || (typeof row.subCategory === 'string' ? row.subCategory : null) || row.subCategoryId || 'N/A';
                                    const faqList = parseFaqs(row.faqs);

                                    return (
                                        <TableRow key={row.id} sx={{
                                            '& td': { borderBottom: '1px solid #F1F5F9' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell align="left" sx={{ color: '#64748B', py: 0.85, px: 1.25, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                                                {(page * rowsPerPage) + idx + 1}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25 }}>
                                                <Avatar
                                                    src={rowImg} alt={rowName} variant="rounded"
                                                    sx={{ width: 38, height: 26, borderRadius: '4px', border: '1px solid #EEE' }}
                                                />
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, maxWidth: 150 }}>
                                                <Typography sx={{ fontSize: '0.73rem', fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={rowName}>
                                                    {rowName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, maxWidth: 130 }}>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={rowLoc}>
                                                    {rowLoc}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1,
                                                        py: 0.25,
                                                        borderRadius: '4px',
                                                        bgcolor: rowType === 'training' ? '#E0F2FE' : '#DCFCE7',
                                                        color: rowType === 'training' ? '#0369A1' : '#15803D',
                                                        fontSize: '0.67rem',
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize'
                                                    }}
                                                >
                                                    {rowType ? rowType.charAt(0).toUpperCase() + rowType.slice(1) : 'Retreat'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1,
                                                        py: 0.25,
                                                        borderRadius: '4px',
                                                        bgcolor: '#E2E8F0',
                                                        color: '#334155',
                                                        fontSize: '0.67rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {rowCatName}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1,
                                                        py: 0.25,
                                                        borderRadius: '4px',
                                                        bgcolor: '#FEF3C7',
                                                        color: '#92400E',
                                                        fontSize: '0.67rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {rowSubName}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, maxWidth: 140 }}>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.heading}>
                                                    {row.heading || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={`${suitList.length} items`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.65rem', height: '20px', borderRadius: '4px', borderColor: '#CBD5E1', color: '#334155' }}
                                                />
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 0.85, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={`${faqList.length} FAQs`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.65rem', height: '20px', borderRadius: '4px', borderColor: '#CBD5E1', color: '#64748B' }}
                                                />
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {row.cuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {row.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(row.createdAt)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(row.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)} sx={{ color: '#64748B', p: 0.5 }}>
                                                    <MoreVertical size={16} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 5, color: '#64748B', fontSize: '0.74rem' }}>
                                        No hubs locations found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <GlobalPagination
                    page={page}
                    pageSize={rowsPerPage}
                    totalRecords={totalRecords}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newSize) => {
                        setRowsPerPage(newSize);
                        setPage(1);
                    }}
                    loading={loading}
                    sx={{
                        border: 'none',
                        borderTop: '1px solid #EEF2F6',
                        borderRadius: 0,
                        mt: 0,
                        py: 1,
                        px: 1.5,
                        boxShadow: 'none'
                    }}
                />
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: 'none',
                        border: '1px solid #E2E8F0',
                        borderRadius: '6px',
                        mt: 0.5,
                        minWidth: 140
                    }
                }}
            >
                <MenuItem onClick={handleViewDetails} sx={{ py: 0.8, px: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                        <Eye size={14} color="#0284C7" />
                    </ListItemIcon>
                    <Typography variant="body2" color="#1E293B" sx={{ fontSize: '0.74rem' }}>View Details</Typography>
                </MenuItem>
                <MenuItem onClick={handleEdit} sx={{ py: 0.8, px: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                        <SquarePen size={14} color="#334155" />
                    </ListItemIcon>
                    <Typography variant="body2" color="#1E293B" sx={{ fontSize: '0.74rem' }}>Edit</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default GetAllHubsLocations;
